import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// Country → language mapping
const COUNTRIES = [
  { countryCode: 'BR', flag: '🇧🇷', name: 'Brasil', lang: 'pt-BR' },
  { countryCode: 'PT', flag: '🇵🇹', name: 'Portugal', lang: 'pt-PT' },
  { countryCode: 'US', flag: '🇺🇸', name: 'United States', lang: 'en-US' },
  { countryCode: 'GB', flag: '🇬🇧', name: 'United Kingdom', lang: 'en-GB' },
  { countryCode: 'DE', flag: '🇩🇪', name: 'Deutschland', lang: 'de' },
  { countryCode: 'FR', flag: '🇫🇷', name: 'France', lang: 'fr' },
  { countryCode: 'IT', flag: '🇮🇹', name: 'Italia', lang: 'it' },
  { countryCode: 'ES', flag: '🇪🇸', name: 'España', lang: 'es' },
  { countryCode: 'MX', flag: '🇲🇽', name: 'México', lang: 'es-MX' },
  { countryCode: 'NL', flag: '🇳🇱', name: 'Nederland', lang: 'nl' },
  { countryCode: 'JP', flag: '🇯🇵', name: '日本', lang: 'ja' },
  { countryCode: 'IN', flag: '🇮🇳', name: 'India', lang: 'hi' },
  { countryCode: 'SA', flag: '🇸🇦', name: 'العربية', lang: 'ar' },
  { countryCode: 'SE', flag: '🇸🇪', name: 'Sverige', lang: 'sv' },
  { countryCode: 'PL', flag: '🇵🇱', name: 'Polska', lang: 'pl' },
  { countryCode: 'MY', flag: '🇲🇾', name: 'Malaysia', lang: 'ms' },
  { countryCode: 'TH', flag: '🇹🇭', name: 'ไทย', lang: 'th' },
  { countryCode: 'RO', flag: '🇷🇴', name: 'România', lang: 'ro' },
  { countryCode: 'IL', flag: '🇮🇱', name: 'ישראל', lang: 'he' },
]

// Map language code → country
function langToCountry(lang: string) {
  return COUNTRIES.find(c => c.lang === lang)
    ?? COUNTRIES.find(c => lang.startsWith(c.lang.split('-')[0]))
    ?? COUNTRIES[2] // fallback US
}

export default function GlobalLanguageSwitcher() {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const current = langToCountry(i18n.language)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function selectCountry(country: typeof COUNTRIES[0]) {
    i18n.changeLanguage(country.lang)
    localStorage.setItem('pakt_language', country.lang)
    const rtl = ['ar', 'he'].some(r => country.lang.startsWith(r))
    document.documentElement.dir = rtl ? 'rtl' : 'ltr'
    document.documentElement.lang = country.lang
    setOpen(false)
  }

  return (
    <div ref={ref} className="fixed bottom-5 right-4 z-50">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-full shadow-md hover:border-gray-300 hover:shadow-lg transition text-sm"
        title="Select country / language"
      >
        <span className="text-xl leading-none">{current.flag}</span>
        <svg className={`w-3 h-3 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-10 right-0 w-52 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selecionar país / Country</p>
          </div>
          <div className="overflow-y-auto max-h-72">
            {COUNTRIES.map(country => (
              <button
                key={country.countryCode}
                onClick={() => selectCountry(country)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition ${
                  current.countryCode === country.countryCode
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl leading-none">{country.flag}</span>
                <span>{country.name}</span>
                {current.countryCode === country.countryCode && (
                  <svg className="w-3.5 h-3.5 ml-auto text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
